import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Shield,
  Plus,
  Edit,
  Trash2,
  Users,
  Eye,
  EyeOff,
  DollarSign,
  X,
  Check,
  AlertTriangle,
  Package
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Drop assigned_driver ids that are not in the current drivers list (deleted / stale). */
function sanitizeAssignedDriverIds(driverIds, driversList) {
  const valid = new Set((driversList || []).map((d) => d?.id).filter(Boolean));
  if (valid.size === 0) return driverIds || [];
  return (driverIds || []).filter((id) => valid.has(id));
}

/** Match backend resolved_admin_type: legacy admins without admin_type are super_admin. */
function resolvedAdminType(session) {
  const raw = session?.admin_type;
  if (typeof raw !== 'string' || !raw.trim()) return 'super_admin';
  return raw.trim().toLowerCase();
}

function canManageCollaborators(session) {
  if (!session) return false;
  if (session.can_manage_collaborators === true) return true;
  return resolvedAdminType(session) === 'super_admin';
}

const SubAdminManagement = ({ drivers = [], orders = [] }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.language || '').startsWith('en') ? 'en-US' : 'es-ES';
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAssignServicesDialog, setShowAssignServicesDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    admin_type: 'sub_admin',
    assigned_drivers: [],
    assigned_services: [],
    can_delete: false,
    can_see_client_prices: false,
    can_see_unassigned: false
  });

  // Driver selection state
  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');

  const loadSessionInfo = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return null;
      const response = await fetch(`${BACKEND_URL}/api/admin/current`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return null;
      const data = await response.json();
      setSessionInfo(data);
      return data;
    } catch (error) {
      console.error('Error loading admin session:', error);
      return null;
    }
  };

  useEffect(() => {
    loadSessionInfo();
    loadSubAdmins();
  }, []);

  const ensureCanManageCollaborators = async () => {
    const session = (await loadSessionInfo()) || sessionInfo;
    if (!canManageCollaborators(session)) {
      const who = session?.email
        ? `${session.email} (${resolvedAdminType(session)})`
        : t('admin_unknown_session');
      toast.error(t('admin_super_admin_required_with_session', { session: who }));
      return false;
    }
    return true;
  };

  const loadSubAdmins = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/sub-admins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSubAdmins(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin_error_load_collaborators'));
      }
    } catch (error) {
      console.error('Error loading sub-admins:', error);
      toast.error(t('admin_error_load_collaborators'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubAdmin = async (e) => {
    e.preventDefault();
    if (!(await ensureCanManageCollaborators())) return;
    
    if (!formData.email || !formData.password || !formData.name) {
      toast.error(t('admin_fill_required'));
      return;
    }

    if (formData.assigned_drivers.length === 0) {
      toast.error(t('admin_assign_at_least_one_driver'));
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/sub-admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          assigned_drivers: sanitizeAssignedDriverIds(formData.assigned_drivers, drivers),
        })
      });

      if (response.ok) {
        toast.success(t('admin_toast_collaborator_created'));
        setShowCreateDialog(false);
        resetForm();
        loadSubAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin_error_create_collaborator'));
      }
    } catch (error) {
      console.error('Error creating sub-admin:', error);
      toast.error(t('admin_error_create_collaborator'));
    }
  };

  const handleUpdateSubAdmin = async (e) => {
    e.preventDefault();
    
    if (!editingAdmin) return;
    if (!(await ensureCanManageCollaborators())) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/sub-admins/${editingAdmin.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          admin_type: formData.admin_type,
          assigned_drivers: sanitizeAssignedDriverIds(formData.assigned_drivers, drivers),
          can_delete: formData.can_delete,
          can_see_client_prices: formData.can_see_client_prices,
          can_see_unassigned: formData.can_see_unassigned
        })
      });

      if (response.ok) {
        toast.success(t('admin_toast_collaborator_updated'));
        setShowEditDialog(false);
        setEditingAdmin(null);
        resetForm();
        loadSubAdmins();
      } else {
        const error = await response.json();
        const detail = error.detail || t('admin_error_update_collaborator');
        if (response.status === 403 && typeof detail === 'string' && detail.startsWith('No se puede modificar')) {
          toast.error(detail);
        } else if (response.status === 403 && typeof detail === 'string' && detail.includes('administrador principal')) {
          toast.error(detail);
        } else {
          toast.error(detail);
        }
      }
    } catch (error) {
      console.error('Error updating sub-admin:', error);
      toast.error(t('admin_error_update_collaborator'));
    }
  };

  const handleDeleteSubAdmin = async (adminId) => {
    if (!window.confirm(t('admin_confirm_delete_collaborator'))) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/sub-admins/${adminId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(t('admin_toast_collaborator_deleted'));
        loadSubAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin_error_delete_collaborator'));
      }
    } catch (error) {
      console.error('Error deleting sub-admin:', error);
      toast.error(t('admin_error_delete_collaborator'));
    }
  };

  const handleEditClick = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      email: admin.email,
      password: '',
      name: admin.name,
      admin_type: admin.admin_type || 'sub_admin',
      assigned_drivers: sanitizeAssignedDriverIds(admin.assigned_drivers || [], drivers),
      assigned_services: admin.assigned_services || [],
      can_delete: admin.can_delete || false,
      can_see_client_prices: admin.can_see_client_prices || false,
      can_see_unassigned: admin.can_see_unassigned || false
    });
    setShowEditDialog(true);
  };

  const handleAssignServicesClick = (admin) => {
    setEditingAdmin(admin);
    setFormData(prev => ({
      ...prev,
      assigned_services: admin.assigned_services || []
    }));
    setShowAssignServicesDialog(true);
  };

  const handleSaveAssignedServices = async () => {
    if (!editingAdmin) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/sub-admins/${editingAdmin.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          assigned_services: formData.assigned_services
        })
      });

      if (response.ok) {
        toast.success(t('admin_toast_services_assigned'));
        setShowAssignServicesDialog(false);
        setEditingAdmin(null);
        loadSubAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin_error_assign_services'));
      }
    } catch (error) {
      console.error('Error saving assigned services:', error);
      toast.error(t('admin_error_assign_services'));
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      admin_type: 'sub_admin',
      assigned_drivers: [],
      assigned_services: [],
      can_delete: false,
      can_see_client_prices: false,
      can_see_unassigned: false
    });
    setDriverSearchTerm('');
    setServiceSearchTerm('');
  };

  const toggleDriverAssignment = (driverId) => {
    setFormData(prev => ({
      ...prev,
      assigned_drivers: prev.assigned_drivers.includes(driverId)
        ? prev.assigned_drivers.filter(id => id !== driverId)
        : [...prev.assigned_drivers, driverId]
    }));
  };

  const toggleServiceAssignment = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      assigned_services: prev.assigned_services.includes(serviceId)
        ? prev.assigned_services.filter(id => id !== serviceId)
        : [...prev.assigned_services, serviceId]
    }));
  };

  const selectAllDrivers = useCallback(() => {
    // Filter to only drivers with valid IDs
    const validDriverIds = (drivers || []).filter(d => d && d.id).map(d => d.id);
    setFormData(prev => ({
      ...prev,
      assigned_drivers: [...validDriverIds]
    }));
  }, [drivers]);

  const clearAllDrivers = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      assigned_drivers: []
    }));
  }, []);

  const filteredDrivers = useMemo(() => (drivers || []).filter(d => 
    d && d.name && (
      d.name.toLowerCase().includes(driverSearchTerm.toLowerCase()) ||
      (d.email && d.email.toLowerCase().includes(driverSearchTerm.toLowerCase()))
    )
  ), [drivers, driverSearchTerm]);

  // Filter orders that don't have a driver (unassigned) for service assignment
  const unassignedOrders = orders.filter(o => !o.driver_id || o.driver_id === '');
  const filteredOrders = unassignedOrders.filter(o => 
    o.id?.toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
    o.pickup_city?.toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
    o.delivery_city?.toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
    o.client_name?.toLowerCase().includes(serviceSearchTerm.toLowerCase())
  );

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver ? driver.name : t('admin_driver_not_found');
  };

  const getServiceSummary = (serviceId) => {
    const order = orders.find(o => o.id === serviceId);
    if (!order) return t('admin_service_not_found');
    return `${order.pickup_city || '?'} → ${order.delivery_city || '?'}`;
  };

  const AdminTypeLabel = ({ type, translate }) => {
    const labels = {
      super_admin: { text: translate('admin_super_admin'), color: 'bg-purple-500' },
      admin_full: { text: translate('admin_full'), color: 'bg-blue-500' },
      sub_admin: { text: translate('admin_sub_admin'), color: 'bg-green-500' },
    };
    const label = labels[type] || { text: type, color: 'bg-gray-500' };
    return (
      <span className={`${label.color} text-white text-xs px-2 py-1 rounded-full`}>
        {label.text}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#36e961]" />
            {t('admin_subadmins_title')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('admin_subadmins_desc')}
          </p>
          {sessionInfo?.email && (
            <p className="text-xs text-gray-500 mt-2">
              {t('admin_session_label')}: {sessionInfo.email}
              {sessionInfo.admin_type ? ` (${resolvedAdminType(sessionInfo)})` : ''}
              {!canManageCollaborators(sessionInfo) && (
                <span className="text-amber-700 font-medium"> — {t('admin_super_admin_required')}</span>
              )}
            </p>
          )}
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateDialog(true); }}
          className="bg-[#36e961] hover:bg-[#2bc950] text-black"
          data-testid="create-subadmin-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('admin_new_collaborator')}
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">{t('admin_what_is_collaborator')}</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>{t('admin_collaborator_bullet1')}</li>
                <li>{t('admin_collaborator_bullet2')}</li>
                <li>{t('admin_collaborator_bullet3')}</li>
                <li>{t('admin_collaborator_bullet4')}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sub-Admins List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">{t('admin_loading_collaborators')}</div>
      ) : subAdmins.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('admin_no_collaborators')}</p>
            <p className="text-sm text-gray-400 mt-1">{t('admin_create_first_collaborator')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {subAdmins.map((admin) => (
            <Card key={admin.id} className="hover:shadow-md transition-shadow" data-testid={`subadmin-card-${admin.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Admin Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-[#0a3042] flex items-center justify-center text-white font-bold">
                        {admin.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{admin.name}</h3>
                        <p className="text-sm text-gray-500">{admin.email}</p>
                      </div>
                      <AdminTypeLabel type={admin.admin_type} translate={t} />
                    </div>
                    
                    {/* Permissions */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${
                        admin.can_see_client_prices ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {admin.can_see_client_prices ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {admin.can_see_client_prices ? t('admin_see_prices') : t('admin_no_see_prices')}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${
                        admin.can_see_unassigned ? 'bg-cyan-100 text-cyan-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {admin.can_see_unassigned ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {admin.can_see_unassigned ? t('admin_see_unassigned') : t('admin_no_see_unassigned')}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${
                        admin.can_delete ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {admin.can_delete ? <Trash2 className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        {admin.can_delete ? t('admin_can_delete') : t('admin_cannot_delete')}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                        <Users className="w-3 h-3" />
                        {admin.assigned_drivers?.length || 0} {t('admin_drivers_count')}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">
                        <Package className="w-3 h-3" />
                        {admin.assigned_services?.length || 0} {t('admin_services_count')}
                      </span>
                    </div>

                    {/* Assigned Drivers */}
                    {admin.assigned_drivers?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 mb-1">{t('admin_assigned_drivers')}</p>
                        <div className="flex flex-wrap gap-1">
                          {admin.assigned_drivers.slice(0, 5).map(driverId => (
                            <span key={driverId} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                              {getDriverName(driverId)}
                            </span>
                          ))}
                          {admin.assigned_drivers.length > 5 && (
                            <span className="text-xs text-gray-500">
                              {t('admin_more_count_short', { count: admin.assigned_drivers.length - 5 })}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Assigned Services */}
                    {admin.assigned_services?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 mb-1">{t('admin_assigned_services_unassigned')}</p>
                        <div className="flex flex-wrap gap-1">
                          {admin.assigned_services.slice(0, 3).map(serviceId => (
                            <span key={serviceId} className="text-xs bg-purple-50 px-2 py-0.5 rounded text-purple-700">
                              {getServiceSummary(serviceId)}
                            </span>
                          ))}
                          {admin.assigned_services.length > 3 && (
                            <span className="text-xs text-gray-500">
                              {t('admin_more_count_short', { count: admin.assigned_services.length - 3 })}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditClick(admin)}
                      data-testid={`edit-subadmin-${admin.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      {t('admin_edit')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAssignServicesClick(admin)}
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      data-testid={`assign-services-${admin.id}`}
                    >
                      <Package className="w-4 h-4 mr-1" />
                      {t('admin_services')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteSubAdmin(admin.id)}
                      data-testid={`delete-subadmin-${admin.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#36e961]" />
              {t('admin_create_collaborator')}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateSubAdmin} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">{t('admin_name_required')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('admin_name_placeholder')}
                  required
                  data-testid="subadmin-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">{t('admin_email_required')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t('admin_email_placeholder')}
                  required
                  data-testid="subadmin-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">{t('admin_password_required')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={t('admin_password_placeholder')}
                  required
                  minLength={8}
                  data-testid="subadmin-password-input"
                />
              </div>
              <div>
                <Label htmlFor="admin_type">{t('admin_admin_type')}</Label>
                <Select
                  value={formData.admin_type}
                  onValueChange={(value) => setFormData({ ...formData, admin_type: value })}
                >
                  <SelectTrigger data-testid="subadmin-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sub_admin">{t('admin_sub_admin_option')}</SelectItem>
                    <SelectItem value="admin_full">{t('admin_full_option')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Permissions */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium mb-3">{t('admin_permissions')}</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="can_see_client_prices"
                    checked={formData.can_see_client_prices}
                    onCheckedChange={(checked) => setFormData({ ...formData, can_see_client_prices: checked })}
                    data-testid="subadmin-see-prices-checkbox"
                  />
                  <Label htmlFor="can_see_client_prices" className="flex flex-col cursor-pointer">
                    <span className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      {t('admin_can_see_prices_invoices')}
                    </span>
                    <span className="text-xs text-gray-500 ml-6">{t('admin_includes_prices_note')}</span>
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="can_see_unassigned"
                    checked={formData.can_see_unassigned}
                    onCheckedChange={(checked) => setFormData({ ...formData, can_see_unassigned: checked })}
                    data-testid="subadmin-see-unassigned-checkbox"
                  />
                  <Label htmlFor="can_see_unassigned" className="flex items-center gap-2 cursor-pointer">
                    <Users className="w-4 h-4 text-blue-600" />
                    {t('admin_can_see_unassigned_services')}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="can_delete"
                    checked={formData.can_delete}
                    onCheckedChange={(checked) => setFormData({ ...formData, can_delete: checked })}
                    data-testid="subadmin-delete-checkbox"
                  />
                  <Label htmlFor="can_delete" className="flex items-center gap-2 cursor-pointer">
                    <Trash2 className="w-4 h-4 text-red-600" />
                    {t('admin_can_delete_services')}
                  </Label>
                </div>
              </div>
            </div>

            {/* Driver Assignment */}
            {formData.admin_type === 'sub_admin' && (
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {t('admin_assigned_drivers_required')}
                  </h4>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={selectAllDrivers}>
                      {t('admin_all_drivers')}
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={clearAllDrivers}>
                      {t('admin_none')}
                    </Button>
                  </div>
                </div>
                
                <Input
                  placeholder={t('admin_search_driver_placeholder')}
                  value={driverSearchTerm}
                  onChange={(e) => setDriverSearchTerm(e.target.value)}
                  className="mb-3"
                  data-testid="driver-search-input"
                />
                
                <div className="max-h-48 overflow-y-auto border rounded bg-white">
                  {filteredDrivers.length === 0 ? (
                    <p className="p-4 text-center text-gray-500 text-sm">{t('admin_no_drivers_available')}</p>
                  ) : (
                    filteredDrivers.map(driver => (
                      <div
                        key={driver.id}
                        className={`flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 ${
                          formData.assigned_drivers.includes(driver.id) ? 'bg-green-50' : ''
                        }`}
                        onClick={() => toggleDriverAssignment(driver.id)}
                      >
                        <input 
                          type="checkbox"
                          checked={formData.assigned_drivers.includes(driver.id)}
                          onChange={() => {}}
                          className="h-4 w-4 rounded border-gray-300 pointer-events-none"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{driver.name}</p>
                          <p className="text-xs text-gray-500">{driver.email}</p>
                        </div>
                        {formData.assigned_drivers.includes(driver.id) && (
                          <Check className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                    ))
                  )}
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  {formData.assigned_drivers.length} {t('admin_drivers_selected')}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-[#36e961] hover:bg-[#2bc950] text-black" data-testid="save-subadmin-btn">
                {t('admin_create_collaborator_btn')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              {t('admin_edit_collaborator')}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateSubAdmin} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-name">{t('admin_name_label')}</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('admin_name_placeholder')}
                />
              </div>
              <div>
                <Label htmlFor="edit-email">{t('admin_email_header')}</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">{t('admin_email_readonly')}</p>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="edit-admin_type">{t('admin_admin_type')}</Label>
                <Select
                  value={formData.admin_type}
                  onValueChange={(value) => setFormData({ ...formData, admin_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sub_admin">{t('admin_sub_admin_option')}</SelectItem>
                    <SelectItem value="admin_full">{t('admin_full_option')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Permissions */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium mb-3">{t('admin_permissions')}</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="edit-can_see_client_prices"
                    checked={formData.can_see_client_prices}
                    onCheckedChange={(checked) => setFormData({ ...formData, can_see_client_prices: checked })}
                  />
                  <Label htmlFor="edit-can_see_client_prices" className="flex flex-col cursor-pointer">
                    <span className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      {t('admin_can_see_prices_invoices')}
                    </span>
                    <span className="text-xs text-gray-500 ml-6">{t('admin_includes_prices_note')}</span>
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="edit-can_see_unassigned"
                    checked={formData.can_see_unassigned}
                    onCheckedChange={(checked) => setFormData({ ...formData, can_see_unassigned: checked })}
                  />
                  <Label htmlFor="edit-can_see_unassigned" className="flex items-center gap-2 cursor-pointer">
                    <Users className="w-4 h-4 text-blue-600" />
                    {t('admin_can_see_unassigned_services')}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="edit-can_delete"
                    checked={formData.can_delete}
                    onCheckedChange={(checked) => setFormData({ ...formData, can_delete: checked })}
                  />
                  <Label htmlFor="edit-can_delete" className="flex items-center gap-2 cursor-pointer">
                    <Trash2 className="w-4 h-4 text-red-600" />
                    {t('admin_can_delete_services')}
                  </Label>
                </div>
              </div>
            </div>

            {/* Driver Assignment */}
            {formData.admin_type === 'sub_admin' && (
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {t('admin_assigned_drivers_heading')}
                  </h4>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={selectAllDrivers}>
                      {t('admin_all_drivers')}
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={clearAllDrivers}>
                      {t('admin_none')}
                    </Button>
                  </div>
                </div>
                
                <Input
                  placeholder={t('admin_search_driver_placeholder')}
                  value={driverSearchTerm}
                  onChange={(e) => setDriverSearchTerm(e.target.value)}
                  className="mb-3"
                />
                
                <div className="max-h-48 overflow-y-auto border rounded bg-white">
                  {filteredDrivers.length === 0 ? (
                    <p className="p-4 text-center text-gray-500 text-sm">{t('admin_no_drivers_available')}</p>
                  ) : (
                    filteredDrivers.map(driver => (
                      <div
                        key={driver.id}
                        className={`flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 ${
                          formData.assigned_drivers.includes(driver.id) ? 'bg-green-50' : ''
                        }`}
                        onClick={() => toggleDriverAssignment(driver.id)}
                      >
                        <input 
                          type="checkbox"
                          checked={formData.assigned_drivers.includes(driver.id)}
                          onChange={() => {}}
                          className="h-4 w-4 rounded border-gray-300 pointer-events-none"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{driver.name}</p>
                          <p className="text-xs text-gray-500">{driver.email}</p>
                        </div>
                        {formData.assigned_drivers.includes(driver.id) && (
                          <Check className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                    ))
                  )}
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  {formData.assigned_drivers.length} {t('admin_drivers_selected')}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => { setShowEditDialog(false); setEditingAdmin(null); }}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                {t('admin_save_changes')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Services Dialog */}
      <Dialog open={showAssignServicesDialog} onOpenChange={setShowAssignServicesDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-600" />
              {t('admin_assign_services_to')} {editingAdmin?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-800">
                <strong>{t('admin_note')}</strong> {t('admin_assign_services_note')}
              </p>
            </div>

            <Input
              placeholder={t('admin_search_service_placeholder')}
              value={serviceSearchTerm}
              onChange={(e) => setServiceSearchTerm(e.target.value)}
              data-testid="service-search-input"
            />
            
            <div className="max-h-64 overflow-y-auto border rounded bg-white">
              {filteredOrders.length === 0 ? (
                <p className="p-4 text-center text-gray-500 text-sm">
                  {t('admin_no_unassigned_services')}
                </p>
              ) : (
                filteredOrders.map(order => (
                  <div
                    key={order.id}
                    className={`flex items-center gap-2 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 ${
                      formData.assigned_services.includes(order.id) ? 'bg-purple-50' : ''
                    }`}
                    onClick={() => toggleServiceAssignment(order.id)}
                  >
                    <Checkbox
                      checked={formData.assigned_services.includes(order.id)}
                      onCheckedChange={() => toggleServiceAssignment(order.id)}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {order.pickup_city || '?'} → {order.delivery_city || '?'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {order.client_name || t('admin_no_client')} • {order.pickup_date ? new Date(order.pickup_date).toLocaleDateString(dateLocale) : t('admin_no_date_short')}
                      </p>
                      <p className="text-xs text-gray-400">ID: {order.id.slice(0, 8)}...</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {order.status === 'pending' ? t('admin_status_pending') : order.status}
                    </span>
                    {formData.assigned_services.includes(order.id) && (
                      <Check className="w-4 h-4 text-purple-600" />
                    )}
                  </div>
                ))
              )}
            </div>
            
            <p className="text-xs text-gray-500">
              {formData.assigned_services.length} {t('admin_services_selected')}
            </p>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => { setShowAssignServicesDialog(false); setEditingAdmin(null); }}>
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleSaveAssignedServices}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {t('admin_save_services')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubAdminManagement;
